import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, MapPin, Package, Box } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { DateInputBR } from '@components/DateInputBR';
import { ProductSearch } from '@components/ProductSearch';
import eventService from '@services/eventService';
import { companyService } from '@services/companyService';
import { useCepSearch } from '@/hooks/useCepSearch';
import { Event, EventFormData } from '@/types/event';
import { Company } from '@/types/company';

// Cadastro do evento: dados, período, endereço (ViaCEP + geolocalização no save),
// produto do convite (catálogo, sem baixa de estoque) e volume padrão dos convites.
export const EventFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { searchCep } = useCepSearch();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Event | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);

  const [form, setForm] = useState<EventFormData>({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    addr_street: '',
    addr_num: '',
    addr_compl: '',
    addr_district: '',
    addr_city: '',
    addr_state: '',
    addr_zip: '',
    invitation_product_sku: '',
    default_volume_height: undefined,
    default_volume_width: undefined,
    default_volume_length: undefined,
    default_volume_weight: undefined,
    company_id: undefined,
    is_active: true,
  });

  const set = (patch: Partial<EventFormData>) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    companyService.getCompanies().then((list: Company[]) => {
      setCompanies(list);
      if (!isEdit && list.length === 1) set({ company_id: list[0].company_id });
    }).catch(() => setCompanies([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    eventService.getEvent(Number(id))
      .then((ev) => {
        setSaved(ev);
        setForm({
          name: ev.name,
          description: ev.description || '',
          start_date: ev.start_date || '',
          end_date: ev.end_date || '',
          addr_street: ev.addr_street || '',
          addr_num: ev.addr_num || '',
          addr_compl: ev.addr_compl || '',
          addr_district: ev.addr_district || '',
          addr_city: ev.addr_city || '',
          addr_state: ev.addr_state || '',
          addr_zip: ev.addr_zip || '',
          invitation_product_sku: ev.invitation_product_sku || '',
          default_volume_height: ev.default_volume_height,
          default_volume_width: ev.default_volume_width,
          default_volume_length: ev.default_volume_length,
          default_volume_weight: ev.default_volume_weight,
          company_id: ev.company_id,
          is_active: ev.is_active,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar evento'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleCepBlur = async () => {
    const clean = (form.addr_zip || '').replace(/\D/g, '');
    if (clean.length !== 8) return;
    const result = await searchCep(clean);
    if (!result || result.erro) return;
    set({
      addr_street: form.addr_street || result.logradouro,
      addr_district: form.addr_district || result.bairro,
      addr_city: result.localidade,
      addr_state: result.uf,
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Informe o nome do evento.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload: EventFormData = {
        ...form,
        addr_zip: (form.addr_zip || '').replace(/\D/g, '') || undefined,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
        invitation_product_sku: form.invitation_product_sku || undefined,
        description: form.description || undefined,
      };
      const result = isEdit
        ? await eventService.updateEvent(Number(id), payload)
        : await eventService.createEvent(payload);
      setSaved(result);
      navigate('/events');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar evento');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-slate-500">Carregando evento...</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={() => navigate('/events')} icon={<ArrowLeft size={16} />} />
        <div>
          <h1 className="text-h1">{isEdit ? 'Editar Evento' : 'Novo Evento'}</h1>
          <p className="text-slate-600">Dados do evento, produto do convite e volume padrão</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      <Card>
        <CardHeader title="Dados do Evento" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Nome *</label>
              <Input value={form.name} onChange={(e) => set({ name: e.target.value })} maxLength={150} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Descrição</label>
              <textarea
                className="w-full input-field text-sm min-h-[70px]"
                value={form.description || ''}
                onChange={(e) => set({ description: e.target.value })}
                maxLength={2000}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data de Início</label>
              <DateInputBR value={form.start_date || ''} onChange={(v: string) => set({ start_date: v })} className="w-full input-field text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Data de Fim</label>
              <DateInputBR value={form.end_date || ''} onChange={(v: string) => set({ end_date: v })} className="w-full input-field text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Empresa (remetente/emissora da NF)</label>
              <select
                className="w-full input-field text-sm"
                value={form.company_id ?? ''}
                onChange={(e) => set({ company_id: e.target.value ? Number(e.target.value) : undefined })}
              >
                <option value="">Selecione...</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end pb-1">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-emerald-600 border-slate-300 rounded"
                  checked={form.is_active !== false}
                  onChange={(e) => set({ is_active: e.target.checked })}
                />
                Evento ativo
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><MapPin size={16} className="text-emerald-600" /> Endereço do Evento</span>}
          subtitle="Informe o CEP para preencher; as coordenadas são geocodificadas ao salvar e alimentam o mapa do planejamento de rota."
        />
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">CEP</label>
              <Input value={form.addr_zip || ''} onChange={(e) => set({ addr_zip: e.target.value })} onBlur={handleCepBlur} maxLength={9} placeholder="00000-000" />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">Logradouro</label>
              <Input value={form.addr_street || ''} onChange={(e) => set({ addr_street: e.target.value })} maxLength={100} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Número</label>
              <Input value={form.addr_num || ''} onChange={(e) => set({ addr_num: e.target.value })} maxLength={10} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Complemento</label>
              <Input value={form.addr_compl || ''} onChange={(e) => set({ addr_compl: e.target.value })} maxLength={100} />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Bairro</label>
              <Input value={form.addr_district || ''} onChange={(e) => set({ addr_district: e.target.value })} maxLength={100} />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="block text-sm font-medium text-slate-700 mb-1">Cidade</label>
              <Input value={form.addr_city || ''} onChange={(e) => set({ addr_city: e.target.value })} maxLength={100} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">UF</label>
              <Input value={form.addr_state || ''} onChange={(e) => set({ addr_state: e.target.value.toUpperCase() })} maxLength={2} />
            </div>
          </div>
          {saved && saved.latitude !== undefined && saved.longitude !== undefined && (
            <p className="mt-3 text-xs text-slate-500">
              Coordenadas geocodificadas: <span className="font-mono">{saved.latitude?.toFixed(6)}, {saved.longitude?.toFixed(6)}</span>
              {saved.geocoding_status === 1 && <span className="text-amber-600 ml-2">(falha na geocodificação — confira o endereço)</span>}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><Package size={16} className="text-emerald-600" /> Produto do Convite</span>}
          subtitle="Produto do catálogo que representa o convite deste evento (sem baixa de estoque). É o item da NF de bonificação e da declaração de conteúdo dos Correios."
        />
        <CardContent>
          <div className="space-y-3">
            <ProductSearch
              onProductSelect={(p) => set({ invitation_product_sku: p.sku })}
              placeholder="Buscar o produto do convite por SKU ou nome..."
            />
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-600">SKU selecionado:</span>
              {form.invitation_product_sku
                ? <span className="font-mono font-medium text-slate-900">{form.invitation_product_sku}</span>
                : <span className="text-slate-400">nenhum</span>}
              {form.invitation_product_sku && (
                <button type="button" className="text-xs text-red-500 hover:underline" onClick={() => set({ invitation_product_sku: '' })}>
                  remover
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title={<span className="flex items-center gap-2"><Box size={16} className="text-emerald-600" /> Volume Padrão do Convite</span>}
          subtitle="Copiado para cada convite ao criar; pode ser alterado individualmente no convite."
        />
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Altura (cm)</label>
              <Input type="number" min={0} step="0.1" value={form.default_volume_height ?? ''} onChange={(e) => set({ default_volume_height: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Largura (cm)</label>
              <Input type="number" min={0} step="0.1" value={form.default_volume_width ?? ''} onChange={(e) => set({ default_volume_width: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Comprimento (cm)</label>
              <Input type="number" min={0} step="0.1" value={form.default_volume_length ?? ''} onChange={(e) => set({ default_volume_length: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Peso (kg)</label>
              <Input type="number" min={0} step="0.01" value={form.default_volume_weight ?? ''} onChange={(e) => set({ default_volume_weight: e.target.value ? Number(e.target.value) : undefined })} />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/events')}>Cancelar</Button>
        <Button onClick={handleSave} disabled={saving} icon={<Save size={16} />}>
          {saving ? 'Salvando...' : 'Salvar Evento'}
        </Button>
      </div>
    </div>
  );
};
